import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { ContactForm } from '@/components/ContactForm';
import { ScrollProgress, ScrollToTop } from '@/components/ScrollAnimations';
import AnimatedPageHero from '@/components/AnimatedPageHero';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { caseStudies } from '@/data/siteData';
import { ArrowRight, Building2, Users, Shield, Cloud, Server, Wifi } from 'lucide-react';

const industryIcons = {
  'Banking': Shield,
  'Telecom / ISP': Wifi,
  'Software / IT': Cloud,
};

export const CaseStudiesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Case Studies - Felix Infotech | IT Success Stories & Client Results</title>
        <meta name="description" content="Discover how Felix Infotech helped enterprises across BFSI, Healthcare, Manufacturing, Telecom, and Education transform their IT infrastructure and achieve measurable results." />
        <meta name="keywords" content="Felix Infotech case studies, IT success stories, enterprise IT transformation, BFSI IT solutions, healthcare IT case study, network infrastructure projects" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://felixinfotech.com/case-studies" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Case Studies - Felix Infotech | IT Success Stories" />
        <meta property="og:description" content="Real-world IT transformation stories across BFSI, Healthcare, Manufacturing, and more." />
        <meta property="og:url" content="https://felixinfotech.com/case-studies" />
        <meta property="og:site_name" content="Felix Infotech" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Felix Infotech Case Studies" />
        <meta name="twitter:description" content="Success stories and measurable IT transformation results." />
      </Helmet>

      <ScrollProgress />
      <Navbar />

      <main>
        <AnimatedPageHero
          title="Success Stories"
          subtitle="Case Studies"
          description="Discover how we've helped businesses transform their technology infrastructure and achieve their goals."
          badge="Case Studies"
          animationType="cloud"
          gradient="from-[#0066CC] to-[#004499]"
          breadcrumbs={[{ label: 'Case Studies' }]}
        />

        {/* Case Studies */}
        <section className="py-16">
          <div className="container mx-auto px-6">
            <div className="space-y-16">
              {caseStudies.map((study, index) => {
                const IconComponent = industryIcons[study.industry] || Server;
                return (
                  <motion.div
                    key={study.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className={`grid lg:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
                  >
                    {/* Stats Card */}
                    <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                      <div className="bg-gradient-to-br from-[#0066CC] to-[#004499] rounded-2xl p-8 text-white shadow-xl">
                        <span className="px-3 py-1 bg-white/20 text-white text-xs font-medium rounded-full mb-4 inline-block">
                          {study.industry}
                        </span>
                        <h3 className="text-xl font-bold mb-4">{study.client}</h3>
                        <div className="grid grid-cols-2 gap-4">
                          {Object.entries(study.stats).map(([key, value]) => (
                            <div key={key} className="bg-white/10 rounded-lg p-4">
                              <p className="text-2xl font-bold">{value}</p>
                              <p className="text-sm text-white/80 capitalize">{key}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-[#0066CC]/10 flex items-center justify-center">
                          <IconComponent className="w-5 h-5 text-[#0066CC]" />
                        </div>
                        <span className="text-sm text-gray-500">{study.industry}</span>
                      </div>
                      
                      <h2 className="text-2xl font-bold text-gray-900 mb-4">{study.title}</h2>
                      
                      <div className="space-y-4 mb-6">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">Challenge</h4>
                          <p className="text-sm text-gray-600">{study.challenge}</p>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-1">Solution</h4>
                          <p className="text-sm text-gray-600">{study.solution}</p>
                        </div>
                      </div>

                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-900 mb-2">Results</h4>
                        <div className="space-y-2">
                          {study.results.map((result, i) => (
                            <div key={i} className="flex items-start gap-2">
                              <span className="text-[#0066CC] font-bold">✓</span>
                              <span className="text-sm text-gray-600">{result}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#0066CC]">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Write Your Success Story?</h2>
            <p className="text-white/80 max-w-2xl mx-auto mb-8">
              Let's discuss how we can help transform your business with the right technology solutions.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-[#0066CC] hover:bg-gray-100 font-semibold">
                Get Started <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        <ContactForm />
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
};

export default CaseStudiesPage;
